<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Notices"
      subtitle="Publish and manage official society notices"
      add-label="Add"
      :show-view="true"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/notice/add')"
      @view="$router.push('/societyadmin/notice/view/' + $event.id)"
      @edit="$router.push('/societyadmin/notice/edit/' + $event.id)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="notices"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Custom Cells -->
      <template #col-title="{ item }">
        <v-menu
          open-on-hover
          bottom
          offset-y
          nudge-bottom="0"
          :close-delay="300"
          max-width="400"
          content-class="premium-menu-tooltip"
          :disabled="!item.title || item.title.length <= 40"
        >
          <template v-slot:activator="{ on, attrs }">
            <span class="notice-title-text" v-bind="attrs" v-on="on">{{
              item.title
            }}</span>
          </template>
          <div class="premium-tooltip-card">
            <div class="premium-tooltip-scroll">{{ item.title }}</div>
          </div>
        </v-menu>
      </template>

      <template #col-description="{ item }">
        <v-menu
          open-on-hover
          bottom
          offset-y
          nudge-bottom="0"
          :close-delay="300"
          max-width="500"
          content-class="premium-menu-tooltip"
          :disabled="!item.description || item.description.length <= 60"
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="notice-desc-text" v-bind="attrs" v-on="on">
              {{ item.description }}
            </div>
          </template>
          <div class="premium-tooltip-card">
            <div class="premium-tooltip-scroll" style="white-space: pre-line">
              {{ item.description }}
            </div>
          </div>
        </v-menu>
      </template>

      <template #col-created_at="{ item }">
        <span class="date-text">{{ item.created_at | formatDateTime }}</span>
      </template>
    </PremiumTable>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Notice"
      message="Are you sure you want to delete this notice?"
      confirm-text="Yes, Delete Notice"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteNotice"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "NoticeList",

  mixins: [filterMixin],

  components: { PremiumTable, ConfirmModal },

  data() {
    return {
      search: "",
      notices: [],
      totalRecords: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      user: {},
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor"]),

    columns() {
      return [
        {
          key: "title",
          label: this.$t("notice.headers.title") || "Title",
          width: "300px",
        },
        {
          key: "description",
          label: this.$t("notice.headers.description") || "Description",
          minWidth: "200px"
        },
        {
          key: "created_at",
          label: this.$t("notice.headers.date") || "Created Date",
          width: "180px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getNotices();
    },
    itemsPerPage() {
      this.getNotices();
    },
    search() {
      this.page = 1;
      this.getNotices();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getNotices();
    },

    getNotices() {
      this.loading = true;
      const sortBy = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      const params = [
        `society_id=${this.user.id}`,
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`,
      ].join("&");

      this.$axios
        .get("societyadmin/notices?" + params)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.notices = data.notices;
            this.totalRecords = data.total;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteNotice() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/notices/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getNotices();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    debounceSearch: debounce(function () {
      this.page = 1;
      this.getNotices();
    }, 500),
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    
    // Initialize persistent filters for this module
    this.initFilters('notice');

    this.getNotices();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Cells ── */
.notice-title-text {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  cursor: default;
}
.notice-desc-text {
  font-size: 13.5px;
  color: var(--tx3);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  cursor: default;
}

.date-text {
  font-size: 13.5px;
  color: var(--tx3);
  font-weight: 500;
}

.premium-tooltip-card {
  background: var(--brand, #30384d);
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}

.premium-tooltip-scroll {
  max-height: 250px;
  overflow-y: auto;
  display: block;
  font-size: 13px;
  line-height: 1.6;
}

/* Custom scrollbar for tooltips */
.premium-tooltip-scroll::-webkit-scrollbar {
  width: 5px;
}
.premium-tooltip-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.premium-tooltip-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
}
.premium-tooltip-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}
</style>
